import { useMemo } from 'react';
import {
  CustomAttributesEntity,
  getCustomAttributesConfig,
} from 'src/graphql/entities/custom-attributes/custom-attributes-config/custom-attributes-config';
import { parseStoredAttributes } from 'src/graphql/entities/custom-attributes/payload';

interface UseCustomAttributeValuesParams {
  entity: CustomAttributesEntity;
  entityId: string | undefined;
}

interface UseCustomAttributeValuesResult {
  parsedValues: Record<string, unknown>;
  payloadError: string | null;
  loading: boolean;
}

/**
 * loads the raw stored custom-attribute payload for one entity and
 * parses it into a `key -> value` map ready for rendering.
 * If parsing fails (malformed payload), `payloadError` is set instead
 * of throwing so the UI can show a fallback state.
 */
export function useCustomAttributeValues({
  entity,
  entityId,
}: UseCustomAttributeValuesParams): UseCustomAttributeValuesResult {
  const { useStoredAttributes } = getCustomAttributesConfig(entity);
  const { customAttributes, loading } = useStoredAttributes(entityId);

  const { values: parsedValues, error: payloadError } = useMemo(
    () => parseStoredAttributes(customAttributes),
    [customAttributes],
  );

  return { parsedValues, payloadError, loading };
}
